// Desktop notifications: the states a person can actually be in, and the
// transitions between them.
//
// ⚠️ The browser permission prompt can be shown ONCE. A denial is sticky and
// can only be undone by digging into site settings, which most people never
// find. So permission is requested only after an explicit click on "Enable
// desktop notifications" — never on load, never on login, never as a side
// effect of installing the app.

import { gatewayApi } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { registrationError, registrationDiagnostics, type RegistrationTrace } from '@/features/shared/components/recovery/registerWorker';

/** Every state the feature can be in, each with something honest to say. */
export type DesktopPushState =
  /** This browser has no Notification or PushManager at all (older Safari, some embedded views). */
  | 'unsupported'
  /** The server has no VAPID keys — the feature is off for this deployment. */
  | 'unavailable'
  /** Supported and available; nobody has asked yet. */
  | 'off'
  /** Subscribed and delivering. */
  | 'on'
  /** The person said no. ⚠️ We cannot ask again; only site settings can undo it. */
  | 'blocked'
  /** Permission granted but the subscription failed — a real state, not a no-op. */
  | 'error';

export interface DesktopPushStatus {
  state: DesktopPushState;
  /** Set when state is 'error', for the message on screen. */
  detail?: string;
}

/**
 * Where this browser remembers that somebody turned desktop notifications OFF.
 *
 * ⚠️ Needed because "has no local subscription" is ALMOST the same question
 * and not quite. disable() unsubscribes the browser, so an explicit opt-out
 * normally leaves nothing to restore — but if the server call succeeded and
 * the browser unsubscribe then failed, a live subscription would still be
 * sitting there and startup reconciliation would resurrect it. Somebody who
 * turned this off must stay off.
 *
 * Per-browser, like the subscription itself. If it is cleared along with site
 * data the subscription is gone too, so the fallback is still correct.
 */
const OPT_OUT_KEY = 'kp.desktopNotifications.optedOut';

const readOptedOut = (): boolean => {
  try { return localStorage.getItem(OPT_OUT_KEY) === '1'; } catch { return false; }
};
const writeOptedOut = (v: boolean): void => {
  try {
    if (v) localStorage.setItem(OPT_OUT_KEY, '1');
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch { /* private mode: the subscription check still governs */ }
};

/** Whether somebody has explicitly turned desktop notifications off here. */
export const hasOptedOut = (): boolean => readOptedOut();

/** Whether this browser can do web push at all. Pure. */
export const browserSupports = (): boolean =>
  typeof window !== 'undefined'
  && 'Notification' in window
  && 'serviceWorker' in navigator
  && 'PushManager' in window;

/**
 * resolveState turns the three things we know into one answer.
 *
 * Pure, so the whole decision table is testable without a browser: this is
 * where "supported but denied" is distinguished from "supported, allowed, not
 * subscribed", and getting that wrong shows somebody an Enable button that
 * can never work.
 */
export const resolveState = (
  supported: boolean,
  serverEnabled: boolean,
  permission: NotificationPermission | null,
  subscribed: boolean,
): DesktopPushState => {
  if (!supported) return 'unsupported';
  if (!serverEnabled) return 'unavailable';
  if (permission === 'denied') return 'blocked';
  // Subscribed wins over permission: a live subscription IS granted permission,
  // and reading them in the other order flickers to 'off' on a slow read.
  if (subscribed) return 'on';
  return 'off';
};

/** base64url VAPID key -> the Uint8Array the PushManager wants. */
export const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

interface KeyResponse { enabled: boolean; public_key: string }

export const fetchVapidKey = async (): Promise<KeyResponse> => {
  const { data } = await gatewayApi.get<KeyResponse>('/api/v1/notifications/web-push/key');
  return data;
};

/** The current subscription for this browser, or null. */
export const currentSubscription = async (): Promise<PushSubscription | null> => {
  if (!browserSupports()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

/**
 * reconcile re-registers a subscription this browser ALREADY has, when the
 * server has lost its row.
 *
 * Runs at authenticated app start — not only on the settings screen. The
 * self-heal was useless where it was: if the server row went missing, nothing
 * arrived until the person happened to open Account -> Notifications, which
 * almost nobody does.
 *
 * ⚠️ IT NEVER TURNS ANYTHING ON. Four conditions, all required:
 *
 *   - the browser can do web push at all;
 *   - permission is ALREADY granted — this never calls requestPermission(),
 *     because the prompt can be shown once and a denial is permanent;
 *   - a live PushSubscription already exists here, so there is something to
 *     restore rather than something to create;
 *   - nobody has explicitly turned it off on this browser.
 *
 * The upsert is keyed on endpoint, so re-sending either confirms what the
 * server had or repairs the gap. Best effort: a failure must never delay or
 * break app start.
 */
export const reconcile = async (): Promise<void> => {
  if (!browserSupports()) return;
  // ⚠️ Reading .permission does NOT prompt. Requesting would.
  if (Notification.permission !== 'granted') return;
  if (readOptedOut()) return;

  // ServiceWorker.ready rather than getRegistration(): at app start the worker
  // may still be installing, and a null registration would be read as "no
  // subscription" and silently skip the repair.
  //
  // ⚠️ BOUNDED. `.catch()` does nothing for a promise that never settles,
  // and `ready` never settles when no worker is registered — so this ran at
  // every app start on such a browser and simply never finished.
  const reg = await readyWithin(READY_TIMEOUT_MS);
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription().catch(() => null);
  if (!sub) return;

  await gatewayApi
    .post('/api/v1/notifications/web-push/subscribe', sub.toJSON())
    .catch(() => { /* the next start tries again; nothing is worse for failing */ });
};

/**
 * enable asks for permission and registers this browser.
 *
 * ⚠️ Call ONLY from a click handler. requestPermission from anywhere else is
 * how a person ends up permanently denied without understanding what they were
 * asked.
 */
/** How long to wait for an active worker before calling it a failure. */
/**
 * Why there is no worker, in words the person can act on.
 *
 * Four causes present identically as "no desktop notifications"; each needs a
 * different response, and a single generic sentence sent people to the wrong
 * one.
 */
export const describeMissingWorker = (
  error: string | null,
  t: RegistrationTrace,
): string => {
  if (error) {
    return `This browser could not install the KaziPeople worker, so it cannot receive desktop notifications: ${error}`;
  }
  if (t.slug === null) {
    // The gate treats an unknown workspace as unknown on purpose — it will not
    // tear a worker off a signed-out browser. Signing in again resolves it.
    return 'Your workspace was not recognised yet, so desktop notifications could not be set up. Sign in again, then retry.';
  }
  if (t.decision && t.decision !== 'register') {
    return 'Desktop notifications are not switched on for this workspace yet.';
  }
  if (!t.attempted) {
    return 'Desktop notifications have not finished starting up on this page. Reload the page and try again.';
  }
  return 'This browser has no active KaziPeople worker yet, so it cannot receive desktop notifications. Reload the page and try again.';
};

export const READY_TIMEOUT_MS = 5000;

/**
 * navigator.serviceWorker.ready, bounded.
 *
 * Resolves with the registration, or null if no worker becomes ready in time.
 * `ready` is a promise that waits forever when nothing is registered, so every
 * caller that treats it as "is there one?" needs this instead.
 */
export const readyWithin = async (
  ms: number,
  ready: Promise<ServiceWorkerRegistration> = navigator.serviceWorker.ready,
): Promise<ServiceWorkerRegistration | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      ready,
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), ms); }),
    ]);
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const enable = async (): Promise<DesktopPushStatus> => {
  if (!browserSupports()) return { state: 'unsupported' };

  const key = await fetchVapidKey().catch(() => null);
  if (!key || !key.enabled || !key.public_key) return { state: 'unavailable' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { state: 'blocked' };

  // ⚠️ navigator.serviceWorker.ready NEVER RESOLVES when nothing is
  // registered — it is not a check, it is a wait. Awaiting it bare meant that
  // on a browser with no worker, enable() hung after the person had already
  // clicked Allow: no subscription, no error, no way to tell. Observed in
  // PRODUCTION on 2026-10-05 ({registrations: 0, permission: "granted",
  // subscription: null}) while mobile push delivered normally.
  //
  // Bounded, and the failure is actionable: a reload is what re-runs the
  // registration, and it is the one thing the person can actually do.
  const reg = await readyWithin(READY_TIMEOUT_MS);
  if (!reg) {
    return {
      state: 'error',
      // If registration actually FAILED, say so: "reload and try again" is
      // useless advice for a browser that refuses to install the worker.
      // Say WHICH of the causes it was. "Reload and try again" is useless
      // advice for a browser that refuses to install, and worse for one whose
      // workspace the gate never resolved.
      detail: describeMissingWorker(registrationError(), registrationDiagnostics()),
    };
  }
  let sub: PushSubscription;
  try {
    // An existing subscription is reused: re-subscribing the same browser
    // returns the same endpoint anyway, and the server upserts on it.
    sub = (await reg.pushManager.getSubscription())
      || (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key.public_key),
      }));
  } catch (e) {
    // Permission granted and subscribe still failed — the push service was
    // unreachable, or the browser refused the key. Saying "enabled" here would
    // be a lie the person only discovers by never receiving anything.
    return { state: 'error', detail: (e as Error)?.message || 'Could not subscribe this browser.' };
  }

  try {
    await gatewayApi.post('/api/v1/notifications/web-push/subscribe', sub.toJSON());
    // They asked for it; forget any previous opt-out.
    writeOptedOut(false);
  } catch (e) {
    // ⚠️ The browser now holds a subscription the server does not know about.
    // Undo it, or this browser is permanently "subscribed" and silent.
    await sub.unsubscribe().catch(() => { /* best effort */ });
    // ⚠️ SURFACE THE SERVER'S OWN MESSAGE. A generic "could not save" hides
    // the one piece of information that makes this fixable — and this repo's
    // rule is that backend messages reach the person through
    // getApiErrorMessage. Writing my own string here cost a full
    // verification round on dev: the panel said "Could not save this
    // browser" while the server was saying exactly what was wrong.
    return {
      state: 'error',
      detail: getApiErrorMessage(e, 'Could not save this browser. Try again.'),
    };
  }
  return { state: 'on' };
};

/**
 * disable removes the subscription from this browser AND from the server.
 *
 * ⚠️ Server first. If the browser unsubscribes and the server call then fails,
 * the row lingers and the endpoint is dead — harmless but noisy. If the server
 * forgets first and the browser call fails, the browser keeps a live
 * subscription nothing will ever send to, which looks identical to "on".
 */
export const disable = async (): Promise<DesktopPushStatus> => {
  // ⚠️ Recorded FIRST, so an opt-out survives any failure below. A person who
  // pressed "turn off" must not be re-subscribed by startup reconciliation
  // because one of the two teardown calls failed.
  writeOptedOut(true);
  const sub = await currentSubscription();
  if (!sub) return { state: 'off' };
  try {
    await gatewayApi.post('/api/v1/notifications/web-push/unsubscribe', { endpoint: sub.endpoint });
  } catch {
    // Keep going: a stale server row is pruned on the first 410.
  }
  await sub.unsubscribe().catch(() => { /* already gone */ });
  return { state: 'off' };
};

/**
 * detachOnSignOut drops this browser's subscription when the session ends or
 * the account changes.
 *
 * ⚠️ Without this a shared machine keeps notifying the last person who used
 * it, with their colleague's approvals arriving on the screen. Best-effort and
 * never allowed to block signing out — a failed detach must not trap somebody
 * in a session they are trying to leave.
 */
export const detachOnSignOut = async (): Promise<void> => {
  try {
    await disable();
  } catch {
    /* signing out matters more than tidying up */
  }
};
